import DocumentSchema from '@components/DocumentSchema';
import type { Table } from 'schema-dts';
import ChartDataTable from '@/pages/CompanyReport/components/Overview/components/ChartDataTable';
import { SnowflakeCellLink } from './SnowflakeCellLink';
import { useOverviewTranslation } from '@/pages/CompanyReport/components/OverviewSection/hooks/useOverviewTranslation';

const getScore = (n: number, precision: number): string =>
  n.toLocaleString(undefined, { maximumFractionDigits: precision });

interface SnowflakeTableProps {
  points: number[];
  tickerSymbol?: string;
}

const SnowflakeTable = ({ points, tickerSymbol }: SnowflakeTableProps) => {
  const t = useOverviewTranslation();
  const header = t('snowflake.table.heading');
  const caption = t('snowflake.table.description', { tickerSymbol });

  return (
    <>
      <DocumentSchema<Table>
        item={{
          '@context': 'https://schema.org',
          '@type': 'Table',
          headline: header,
          about: caption,
        }}
      />
      <ChartDataTable
        data-cy-id="overview-snowflake-table"
        columnTextAlignments="l"
      >
        <caption>{caption}</caption>
        <thead>
          <tr>
            <th colSpan={2}>{header}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <SnowflakeCellLink
                section="valuation"
                text={t('snowflake.table.valuation')}
              />
            </td>
            <td>
              {points && points.length > 0 ? getScore(points[0] - 1, 2) : 0}/6
            </td>
          </tr>
          <tr>
            <td>
              <SnowflakeCellLink
                section="future"
                text={t('snowflake.table.future')}
              />
            </td>
            <td>
              {points && points.length > 1 ? getScore(points[1] - 1, 2) : 0}/6
            </td>
          </tr>
          <tr>
            <td>
              <SnowflakeCellLink
                section="past"
                text={t('snowflake.table.past')}
              />
            </td>
            <td>
              {points && points.length > 2 ? getScore(points[2] - 1, 2) : 0}/6
            </td>
          </tr>
          <tr>
            <td>
              <SnowflakeCellLink
                section="health"
                text={t('snowflake.table.health')}
              />
            </td>
            <td>
              {points && points.length > 3 ? getScore(points[3] - 1, 2) : 0}/6
            </td>
          </tr>
          <tr>
            <td>
              <SnowflakeCellLink
                section="dividend"
                text={t('snowflake.table.dividends')}
              />
            </td>
            <td>
              {points && points.length > 4 ? getScore(points[4] - 1, 2) : 0}/6
            </td>
          </tr>
        </tbody>
      </ChartDataTable>
    </>
  );
};

export default SnowflakeTable;
